import { useState, useEffect } from 'react';
import { Download, TrendingUp, TrendingDown, Minus, Calendar, DollarSign } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface PLLineItem {
  id: string;
  name: string;
  type: 'header' | 'line_item' | 'subtotal' | 'calculated';
  category?: string;
  level: number;
  value?: number;
  formula?: string;
  format?: string;
  highlight?: boolean;
  bold?: boolean;
  children?: PLLineItem[];
}

interface PLStatement {
  id: string;
  organizationId: string;
  templateId?: string;
  statementType: string;
  periodStart: string;
  periodEnd: string;
  periodType: string;
  scenario: string;
  data: {
    lineItems: PLLineItem[];
  };
  totals: {
    totalRevenue: number;
    grossProfit: number;
    grossMargin: number;
    totalOperatingExpenses: number;
    operatingIncome: number;
    netIncome: number;
    netMargin: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface VarianceItem {
  id: string;
  name: string;
  current: number;
  comparison: number;
  variance: number;
  variancePercent: number;
  isFavorable: boolean;
}

interface VarianceAnalysis {
  id: string;
  analysisType: string;
  variances: {
    lineItems: VarianceItem[];
  };
  summary: {
    totalRevenue: { current: number; comparison: number; variance: number; variancePercent: number };
    grossProfit: { current: number; comparison: number; variance: number; variancePercent: number };
    operatingIncome: { current: number; comparison: number; variance: number; variancePercent: number };
    netIncome: { current: number; comparison: number; variance: number; variancePercent: number };
  };
  createdAt: string;
}

interface ProfitLossStatementProps {
  statementId?: string;
  showVariance?: boolean;
  comparisonStatementId?: string;
  onGenerate?: () => void;
}

const ProfitLossStatement = ({
  statementId,
  showVariance = false,
  comparisonStatementId,
  onGenerate
}: ProfitLossStatementProps) => {
  const [statement, setStatement] = useState<PLStatement | null>(null);
  const [variance, setVariance] = useState<VarianceAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'statement' | 'variance'>('statement');

  useEffect(() => {
    if (statementId) {
      loadStatement();
    }
  }, [statementId]);

  useEffect(() => {
    if (showVariance && statementId && comparisonStatementId) {
      loadVarianceAnalysis();
    }
  }, [showVariance, statementId, comparisonStatementId]);

  const loadStatement = async () => {
    if (!statementId) return;

    setLoading(true);
    try {
      const response = await api.get(`/profit-loss/statements/${statementId}`);
      setStatement(response.data.data);
    } catch (error: any) {
      toast.error('Failed to load P&L statement');
      console.error('Error loading statement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVarianceAnalysis = async () => {
    if (!statementId || !comparisonStatementId) return;

    try {
      const response = await api.post('/profit-loss/variance-analysis', {
        statementId,
        comparisonStatementId,
        analysisType: 'period_vs_period',
      });
      setVariance(response.data.data);
    } catch (error: any) {
      toast.error('Failed to load variance analysis');
      console.error('Error loading variance:', error);
    }
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '$0.00';

    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return value < 0 ? `($${formatted})` : `$${formatted}`;
  };

  const formatPercent = (value: number | undefined): string => {
    if (value === undefined || value === null) return '0.0%';
    return `${value.toFixed(1)}%`;
  };

  const formatVariance = (value: number): string => {
    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (value > 0) return `$${formatted}`;
    if (value < 0) return `($${formatted})`;
    return '$0.00';
  };

  const getVarianceIcon = (variance: number, isFavorable: boolean) => {
    if (variance === 0) return <Minus className="w-4 h-4 text-neutral-500" />;
    if (isFavorable) return <TrendingUp className="w-4 h-4 text-success-600" />;
    return <TrendingDown className="w-4 h-4 text-alert-600" />;
  };

  const renderLineItem = (item: PLLineItem, index: number) => {
    const isHeader = item.type === 'header';
    const isSubtotal = item.type === 'subtotal' || item.type === 'calculated';
    const indentClass = `pl-${item.level * 6}`;

    return (
      <div key={item.id || index}>
        <div
          className={`grid grid-cols-2 py-2 border-b border-neutral-100 hover:bg-neutral-50 ${
            item.highlight ? 'bg-primary-50' : ''
          } ${isSubtotal ? 'border-t-2 border-neutral-300 font-semibold' : ''}`}
        >
          <div className={`${indentClass} ${item.bold || isSubtotal ? 'font-bold' : ''} ${isHeader ? 'font-semibold text-neutral-700' : 'text-neutral-900'}`}>
            {item.name}
          </div>
          <div className={`text-right ${item.bold || isSubtotal ? 'font-bold' : ''} text-neutral-900`}>
            {item.format === 'percentage' ? formatPercent(item.value) : formatCurrency(item.value)}
          </div>
        </div>

        {item.children && item.children.map((child, idx) => renderLineItem(child, idx))}
      </div>
    );
  };

  const renderVarianceItem = (item: VarianceItem) => {
    const varianceColor = item.isFavorable ? 'text-success-600' : 'text-alert-600';

    return (
      <div
        key={item.id}
        className="grid grid-cols-5 gap-4 py-2 border-b border-neutral-100 hover:bg-neutral-50"
      >
        <div className="col-span-2 text-neutral-900">{item.name}</div>
        <div className="text-right text-neutral-900">{formatCurrency(item.current)}</div>
        <div className="text-right text-neutral-900">{formatCurrency(item.comparison)}</div>
        <div className={`text-right ${varianceColor} flex items-center justify-end space-x-2`}>
          {getVarianceIcon(item.variance, item.isFavorable)}
          <span>{formatVariance(item.variance)}</span>
          <span className="text-sm">({formatPercent(item.variancePercent)})</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="card text-center py-12">
        <DollarSign className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No P&L Statement</h3>
        <p className="text-neutral-600 mb-4">Generate a P&L statement to get started</p>
        {onGenerate && (
          <button onClick={onGenerate} className="btn btn-primary">
            Generate P&L Statement
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Profit & Loss Statement
            </h2>
            <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-600">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>
                  {new Date(statement.periodStart).toLocaleDateString()} -{' '}
                  {new Date(statement.periodEnd).toLocaleDateString()}
                </span>
              </div>
              <span>•</span>
              <span className="capitalize">{statement.periodType}</span>
              <span>•</span>
              <span className="capitalize bg-primary-100 text-primary-800 px-2 py-1 rounded">
                {statement.scenario}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {showVariance && variance && (
              <div className="flex border border-neutral-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('statement')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'statement'
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  Statement
                </button>
                <button
                  onClick={() => setViewMode('variance')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'variance'
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  Variance Analysis
                </button>
              </div>
            )}

            <button className="btn btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
            <p className="text-sm text-primary-700 mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-primary-900">
              {formatCurrency(statement.totals.totalRevenue)}
            </p>
          </div>

          <div className="p-4 bg-success-50 rounded-lg border border-success-200">
            <p className="text-sm text-success-700 mb-1">Gross Profit</p>
            <p className="text-2xl font-bold text-success-900">
              {formatCurrency(statement.totals.grossProfit)}
            </p>
            <p className="text-xs text-success-600 mt-1">
              {formatPercent(statement.totals.grossMargin)} margin
            </p>
          </div>

          <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <p className="text-sm text-neutral-700 mb-1">Operating Income</p>
            <p className="text-2xl font-bold text-neutral-900">
              {formatCurrency(statement.totals.operatingIncome)}
            </p>
          </div>

          <div className="p-4 bg-neutral-900 rounded-lg">
            <p className="text-sm text-neutral-300 mb-1">Net Income</p>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(statement.totals.netIncome)}
            </p>
            <p className="text-xs text-neutral-400 mt-1">
              {formatPercent(statement.totals.netMargin)} margin
            </p>
          </div>
        </div>
      </div>

      {/* Statement View */}
      {viewMode === 'statement' && (
        <div className="card">
          <div className="grid grid-cols-2 py-3 border-b-2 border-neutral-300 font-semibold text-sm text-neutral-700 bg-neutral-50">
            <div>Account</div>
            <div className="text-right">Amount</div>
          </div>

          <div className="divide-y divide-neutral-100">
            {statement.data.lineItems.map((item, index) => renderLineItem(item, index))}
          </div>
        </div>
      )}

      {/* Variance Analysis View */}
      {viewMode === 'variance' && variance && (
        <div className="card">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Variance Analysis
            </h3>

            {/* Summary Variances */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {Object.entries(variance.summary).map(([key, value]) => {
                const isFavorable = value.variance > 0;
                return (
                  <div
                    key={key}
                    className={`p-4 rounded-lg border ${
                      isFavorable ? 'bg-success-50 border-success-200' : 'bg-alert-50 border-alert-200'
                    }`}
                  >
                    <p className={`text-sm mb-1 ${isFavorable ? 'text-success-700' : 'text-alert-700'}`}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </p>
                    <div className="flex items-center space-x-2">
                      {getVarianceIcon(value.variance, isFavorable)}
                      <p className={`text-xl font-bold ${isFavorable ? 'text-success-900' : 'text-alert-900'}`}>
                        {formatVariance(value.variance)}
                      </p>
                    </div>
                    <p className={`text-xs mt-1 ${isFavorable ? 'text-success-600' : 'text-alert-600'}`}>
                      {formatPercent(value.variancePercent)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Variance Table */}
          <div className="grid grid-cols-5 gap-4 py-3 border-b-2 border-neutral-300 font-semibold text-sm text-neutral-700 bg-neutral-50">
            <div className="col-span-2">Line Item</div>
            <div className="text-right">Current</div>
            <div className="text-right">Comparison</div>
            <div className="text-right">Variance</div>
          </div>

          <div className="divide-y divide-neutral-100">
            {variance.variances.lineItems.map((item) => renderVarianceItem(item))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLossStatement;
